import React, { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import type { ToolchainData, ConfigFormData } from 'shared/types/configuration-service';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from 'shared/ui/form';
import { Checkbox } from 'shared/ui/checkbox';
import { InfoTooltip } from 'shared/ui/tooltips';
import FormControlWithLabel from 'shared/ui/form-control-with-label';
import { Input } from 'shared/ui/input';
import { useFormContext } from 'react-hook-form';
import { useGetLanguageHelper } from 'features/change-config/hooks/useGetLanguageHelper';
import type { ISuggestion } from 'shared/ui/input-with-suggestions';
import InputWithSuggestions from 'shared/ui/input-with-suggestions';
import type { IValidateProject, IValidField } from '@/enteties/config/model';
import type { IFile } from 'shared/types/file-storage-types';
import type { UseMutateFunction } from '@tanstack/react-query';
import { DOCS_DOMAIN } from '@/shared/lib/util';
import { Link } from '@/shared/ui/link';
type ConfigFieldsProps = {
  toolchainDataInit: ToolchainData;
  environment: string;
  toolchainName: string;
  isDisabled?: boolean;
  suggestions: IValidateProject | undefined;
  mutateValidate?: UseMutateFunction<IValidateProject, Error, ConfigFormData, unknown>;
};
const BoolField = memo(function BoolField({
  isDisabled,
  fieldName,
  section
}: {
  isDisabled: boolean;
  fieldName: string;
  section: string;
}) {
  const form = useFormContext();
  return <FormField disabled={isDisabled} name={`${section}.${fieldName}`} control={form.control} render={({
    field
  }) => <FormItem className="flex flex-row items-start space-x-4 space-y-0 pt-3">
          <FormLabel>{fieldName}</FormLabel>
          <FormControl>
            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
          </FormControl>
        </FormItem>} />;
});
const TextFormField = memo(function TextFormField({
  fieldName,
  blockedBy,
  environment,
  toolchainName,
  section,
  isDisabled,
  suggestion,
  handleValidate
}: {
  fieldName: string;
  blockedBy: string | null;
  section: string;
  environment: string;
  toolchainName: string;
  isDisabled: boolean;
  suggestion?: IValidField;
  handleValidate: () => void;
}) {
  const form = useFormContext();
  const formValues = form.watch();
  const [tooltips] = useGetLanguageHelper();
  const convertedSuggestion = useMemo((): ISuggestion[] | undefined => {
    if (suggestion?.isValid) return undefined;
    if (suggestion?.validationType === 'FILE_DISCOVERY') {
      // For FILE_DISCOVERY, suggestions are Array<IFile>
      return (suggestion.suggestions as IFile[]).map(s => ({
        value: s.path,
        label: s.path
      }));
    }
    if (suggestion?.validationType === 'FILE_DISCOVERY_IN_START_CMD') {
      // For FILE_DISCOVERY_IN_START_CMD, suggestions are Array<string>
      return (suggestion.suggestions as string[]).map(s => ({
        value: s,
        label: s
      }));
    }
    return undefined;
  }, [suggestion]);
  const customOnBlur = useCallback(async (onBlur: () => void) => {
    form.clearErrors(`${section}.${fieldName}`);
    onBlur();
    handleValidate();
  }, [handleValidate, form]);

  // Fix the type error by safely checking if blockedBy exists
  const isFieldDisabled = useMemo(() => {
    return isDisabled || !!blockedBy && !!formValues?.[section]?.[blockedBy as keyof (typeof formValues)[typeof section]];
  }, [blockedBy, formValues, isDisabled, section]);
  useEffect(() => {
    if (isFieldDisabled && blockedBy) {
      form.clearErrors(`${section}.${fieldName}`);
    }
  }, [isFieldDisabled, blockedBy, fieldName, section]);
  return <FormField disabled={isFieldDisabled} name={section === 'meta' && fieldName === 'version' ? `${section}.toolchain.${fieldName}` : `${section}.${fieldName}`} control={form.control} render={({
    field
  }) => <FormItem>
          <FormControlWithLabel text={fieldName}>
            <FormControl>
              <InputWithSuggestions suggestions={convertedSuggestion} {...field} onBlur={() => customOnBlur(field.onBlur)} endIcon={<InfoTooltip text={tooltips?.[environment]?.[toolchainName
        // @ts-ignore
        ]?.[section]?.[fieldName]?.info ?? tooltips?.[environment]?.[toolchainName]?.[section]?.[fieldName] ?? tooltips?.[environment]?.[section]?.[fieldName
        // @ts-ignore
        ]?.info ?? tooltips?.[environment]?.[section]?.[fieldName]} />} />
            </FormControl>
          </FormControlWithLabel>
          <FormMessage isParsed />
        </FormItem>} />;
});
export const ConfigFields: React.FC<ConfigFieldsProps> = ({
  toolchainDataInit,
  environment,
  toolchainName,
  isDisabled = false,
  suggestions,
  mutateValidate
}) => {
  const [tooltips] = useGetLanguageHelper();
  const form = useFormContext();
  const {
    unregister,
    setValue
  } = form;
  const formValues = form.watch();
  const handleKeyChange = useCallback((section: string, fieldName: string, key: string, value: string) => {
    unregister(`${section}.${fieldName}`);
    setValue(`${section}.${fieldName}`, {
      [key]: value
    });
  }, [setValue, unregister]);
  const handleValidate = useCallback(() => {
    if (mutateValidate) {
      const config = form.getValues();
      mutateValidate(config);
    }
  }, [mutateValidate, form]);
  const generateFields = useMemo(() => {
    const toolchainData = toolchainDataInit;

    // Remove servicePort if exists
    if (toolchainData?.run?.servicePort) {
      delete toolchainData?.run?.servicePort;
    }
    if (!toolchainData) return null;
    return <>
        {/* @ts-ignore */}
        {tooltips?.[environment]?.[toolchainName]?.docs ? <div className="bg-accent rounded-md px-6 py-3 text-sm">
            <Link
        // @ts-ignore
        href={`${DOCS_DOMAIN}${tooltips?.[environment]?.[toolchainName]?.docs}`} docs>
              Документация
            </Link>
          </div> : null}
        {Object.keys(toolchainData).map(section => <Fragment key={section}>
            {toolchainData[section] && <div className="py-4 flex flex-col gap-2">
                {toolchainData[section] && <h1 className="mb-2">{section}</h1>}
                {toolchainData[section] && Object.entries(toolchainData[section]).map(([fieldName, fieldData]) => <div key={fieldName} className="">
                        {/* Conditional rendering for different field types */}
                        {fieldData.type === 'Bool' ? <BoolField isDisabled={isDisabled} fieldName={fieldName} section={section} /> : fieldData.type === 'Map' ? <div className="mb-2">
                            <div className="flex flex-row items-center gap-2">
                              {fieldName}
                              <InfoTooltip text={tooltips?.[environment]?.[toolchainName]?.[section]?.[fieldName] ?? tooltips?.[environment]?.[section]?.[fieldName]} />
                            </div>
                            <div>
                              <FormField disabled={isDisabled} name={`${section}.${fieldName}`} control={form.control} render={({
                  field
                }) => <FormItem>
                                    <FormControlWithLabel text="Key:">
                                      <FormControl>
                                        <Input value={field.value ? Object.entries(field.value)[0][0] : ''} onChange={event => handleKeyChange(section, fieldName, event.target.value, field.value ? Object.entries(field.value)[0][1] as string : '')} />
                                      </FormControl>
                                    </FormControlWithLabel>
                                  </FormItem>} />

                              <FormField disabled={isDisabled} name={`${section}.${fieldName}`} control={form.control} render={({
                  field
                }) => <FormItem>
                                    <FormControlWithLabel text="Value:">
                                      <FormControl>
                                        <Input value={field.value ? Object.entries(field.value)[0][1] as string : ''} onChange={event => handleKeyChange(section, fieldName, field.value ? Object.entries(field.value)[0][0] : '', event.target.value)} />
                                      </FormControl>
                                    </FormControlWithLabel>
                                  </FormItem>} />
                            </div>
                          </div> : <TextFormField fieldName={fieldName} section={section} isDisabled={isDisabled} blockedBy={fieldData.blockedBy} environment={environment} toolchainName={toolchainName} suggestion={suggestions?.[section as 'build' | 'run']?.[fieldName]} handleValidate={handleValidate} />}
                      </div>)}
              </div>}
          </Fragment>)}
      </>;
  }, [toolchainDataInit, form.control, tooltips, environment, toolchainName, handleKeyChange, formValues, suggestions]);
  return <>{generateFields}</>;
};